import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
  PageViewport,
  RenderTask,
  TextLayer,
} from 'pdfjs-dist';
import {
  type JSX,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import { loadPdfjs, type PdfjsModule } from '@/lib/pdfjs.js';
import { cn } from '@/lib/utils.js';
import { logger } from '@/logger.js';

import { NoPreview } from './NoPreview.js';
import './pdf-text-layer.css';

/** A loaded document and the pdf.js module that opened it, so no page has to import it again. */
type OpenDocument = { pdfjs: PdfjsModule; document: PDFDocumentProxy };

/** Where the document load stands for the url currently on screen. */
type DocumentState =
  | { status: 'loading' }
  | { status: 'error' }
  | ({ status: 'ready' } & OpenDocument);

/** The three nodes a page is built from: the box that reserves its space, the raster, and the text. */
type PageElements = {
  wrapper: HTMLDivElement;
  canvas: HTMLCanvasElement;
  textContainer: HTMLDivElement;
};

/** A page's drawing and text work, cancelled as one. */
type PageWork = { cancel: () => void };

/** Canvas pixels per CSS pixel. Capped so a 3x phone screen does not triple the raster memory. */
const MAX_PIXEL_RATIO = 2;

/** How far below the fold a page starts drawing, so scrolling meets a page that is already there. */
const PRERENDER_MARGIN = '256px';

/** How long the panel must hold still after a resize before the pages are drawn again. */
const RESIZE_SETTLE_MS = 150;

/**
 * Start loading the document behind `url`. `destroy` aborts a load still in flight and frees a document
 * that has already arrived; the promise then settles to `null` instead of an error, so a caller that
 * gave up never hears about it.
 */
function openDocument(url: string): {
  promise: Promise<OpenDocument | null>;
  destroy: () => void;
} {
  let loadingTask: PDFDocumentLoadingTask | null = null;
  let destroyed = false;
  const promise = loadPdfjs()
    .then(async (pdfjs) => {
      if (destroyed) return null;
      loadingTask = pdfjs.getDocument({ url });
      const document = await loadingTask.promise;
      return destroyed ? null : { pdfjs, document };
    })
    .catch((error: unknown) => {
      if (destroyed) return null;
      throw error;
    });
  const destroy = (): void => {
    destroyed = true;
    void loadingTask?.destroy();
  };
  return { promise, destroy };
}

/**
 * The document for `url`, dropped when the url changes or the view unmounts. The result is keyed by url
 * so a new url reads as loading on the very next render, with no reset effect.
 */
function usePdfDocument(url: string): DocumentState {
  const [loaded, setLoaded] = useState<{
    url: string;
    state: DocumentState;
  } | null>(null);
  useEffect(() => {
    const load = openDocument(url);
    void load.promise
      .then((opened) => {
        if (opened !== null)
          setLoaded({ url, state: { status: 'ready', ...opened } });
      })
      .catch((error: unknown) => {
        logger.warn('PdfDocumentView: PDF load failed', error);
        setLoaded({ url, state: { status: 'error' } });
      });
    return load.destroy;
  }, [url]);
  return loaded?.url === url ? loaded.state : { status: 'loading' };
}

/**
 * The scroll container's width in CSS pixels, re-read when the panel is resized. The side panel is
 * user-resizable, and a canvas drawn for one width is only stretched by CSS at another — blurry when
 * widened — so a settled new width redraws the pages. `0` until the first measurement.
 */
function useContainerWidth(
  container: RefObject<HTMLDivElement | null>,
): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = container.current;
    if (element === null) return;
    setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    let settle: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver(() => {
      if (settle !== null) clearTimeout(settle);
      settle = setTimeout(
        () => setWidth(element.clientWidth),
        RESIZE_SETTLE_MS,
      );
    });
    observer.observe(element);
    return () => {
      if (settle !== null) clearTimeout(settle);
      observer.disconnect();
    };
  }, [container]);
  return width;
}

/**
 * The shape a page borrows to reserve its space before it has been fetched: the first page that arrives
 * hands its own over, and pages of one document are almost always the same size. Keyed by document, so
 * a new document starts over instead of reserving space with the last one's shape.
 */
function useFallbackAspectRatio(
  document: PDFDocumentProxy | null,
): [number | null, (aspectRatio: number) => void] {
  const [known, setKnown] = useState<{
    document: PDFDocumentProxy;
    aspectRatio: number;
  } | null>(null);
  const remember = useCallback(
    (aspectRatio: number) => {
      if (document === null) return;
      setKnown((current) =>
        current?.document === document ? current : { document, aspectRatio },
      );
    },
    [document],
  );
  return [known?.document === document ? known.aspectRatio : null, remember];
}

/**
 * The two viewports a page is drawn with. `cssViewport` fits the page to the container width, and
 * `canvasViewport` multiplies that by the device pixel ratio, which is what keeps text crisp on a HiDPI
 * screen. The text layer has to be laid out with the CSS one — the pixels the page really occupies on
 * screen — or every span lands at a multiple of its offset.
 */
function fittedViewports(
  page: PDFPageProxy,
  containerWidth: number,
): {
  cssScale: number;
  cssViewport: PageViewport;
  canvasViewport: PageViewport;
} {
  const baseViewport = page.getViewport({ scale: 1 });
  const width = containerWidth || baseViewport.width;
  const cssScale = width / baseViewport.width;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  return {
    cssScale,
    cssViewport: page.getViewport({ scale: cssScale }),
    canvasViewport: page.getViewport({ scale: cssScale * pixelRatio }),
  };
}

/** Width over height of the unscaled page, the form CSS `aspect-ratio` takes. */
function pageAspectRatio(page: PDFPageProxy): number {
  const viewport = page.getViewport({ scale: 1 });
  return viewport.width / viewport.height;
}

/**
 * Give the canvas its final pixel size before anything is drawn. With `width: 100%` in CSS the height
 * follows the aspect ratio, so the page occupies its real space in the scroll box from the start.
 */
function reserveSpace(canvas: HTMLCanvasElement, viewport: PageViewport): void {
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
}

/** Rasterise the page into its canvas. Returns `null` when the canvas has no 2d context (jsdom). */
function drawPage(
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  viewport: PageViewport,
): RenderTask | null {
  const context = canvas.getContext('2d');
  if (context === null) return null;
  return page.render({ canvasContext: context, viewport });
}

/**
 * Lay the page's text over the canvas as transparent spans, which is what gives a drawn page selection,
 * copy and Ctrl+F. The container keeps only the spans of the current draw, so a redraw at a new width
 * starts from an empty one.
 */
function startTextLayer(
  pdfjs: PdfjsModule,
  page: PDFPageProxy,
  container: HTMLDivElement,
  viewport: PageViewport,
): TextLayer {
  container.replaceChildren();
  return new pdfjs.TextLayer({
    textContentSource: page.streamTextContent(),
    container,
    viewport,
  });
}

/**
 * Draw the page and lay its text over it. pdf.js reads `--scale-factor` off the wrapper to size the text
 * container and every span, so that property carries the CSS fit and never the HiDPI multiple the canvas
 * is rasterised at. Cancelling either half rejects its promise, so both are observed and only a failure
 * that was not cancelled is worth reporting.
 */
function paintPage(
  pdfjs: PdfjsModule,
  page: PDFPageProxy,
  elements: PageElements,
  containerWidth: number,
): PageWork {
  let cancelled = false;
  const { cssScale, cssViewport, canvasViewport } = fittedViewports(
    page,
    containerWidth,
  );
  elements.wrapper.style.setProperty('--scale-factor', String(cssScale));
  reserveSpace(elements.canvas, canvasViewport);
  const renderTask = drawPage(page, elements.canvas, canvasViewport);
  const textLayer = startTextLayer(
    pdfjs,
    page,
    elements.textContainer,
    cssViewport,
  );
  const report = (error: unknown): void => {
    if (!cancelled) logger.warn('PdfDocumentView: page render failed', error);
  };
  void renderTask?.promise.catch(report);
  void textLayer.render().catch(report);
  return {
    cancel: () => {
      cancelled = true;
      renderTask?.cancel();
      textLayer.cancel();
    },
  };
}

/**
 * Run `onVisible` once the element scrolls near the viewport, or at once where the browser has no
 * `IntersectionObserver`. Drawing every page of a long document up front stalls the main thread and
 * holds a raster per page in memory; waiting for each page to come near the fold bounds both.
 */
function whenVisible(
  element: Element,
  onVisible: () => void,
): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') {
    onVisible();
    return null;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      onVisible();
    },
    { rootMargin: PRERENDER_MARGIN },
  );
  observer.observe(element);
  return observer;
}

/**
 * Fetch the page and paint it once its box comes near the viewport, and not before: `getPage` is what
 * makes pdf.js parse a page, so asking for every page up front is the work this defers. The returned
 * function cancels everything still in flight.
 */
function renderPage(
  { pdfjs, document }: OpenDocument,
  pageNumber: number,
  elements: PageElements,
  containerWidth: number,
  onPageArrived: (aspectRatio: number) => void,
): () => void {
  let cancelled = false;
  let work: PageWork | null = null;
  const observer = whenVisible(elements.wrapper, () => {
    void document
      .getPage(pageNumber)
      .then((page) => {
        if (cancelled) return;
        onPageArrived(pageAspectRatio(page));
        work = paintPage(pdfjs, page, elements, containerWidth);
      })
      .catch((error: unknown) => {
        if (!cancelled)
          logger.warn('PdfDocumentView: page render failed', error);
      });
  });
  return () => {
    cancelled = true;
    observer?.disconnect();
    work?.cancel();
  };
}

/** One page of the document, holding its space until it scrolls into view and is drawn. */
function PdfPage({
  opened,
  pageNumber,
  containerWidth,
  fallbackAspectRatio,
  onPageArrived,
}: {
  opened: OpenDocument;
  pageNumber: number;
  containerWidth: number;
  fallbackAspectRatio: number | null;
  onPageArrived: (aspectRatio: number) => void;
}): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textContainerRef = useRef<HTMLDivElement | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    const textContainer = textContainerRef.current;
    if (wrapper === null || canvas === null || textContainer === null) return;
    return renderPage(
      opened,
      pageNumber,
      { wrapper, canvas, textContainer },
      containerWidth,
      (arrived) => {
        setAspectRatio(arrived);
        onPageArrived(arrived);
      },
    );
  }, [opened, pageNumber, containerWidth, onPageArrived]);
  const reserved = aspectRatio ?? fallbackAspectRatio;
  return (
    <div
      ref={wrapperRef}
      className="relative w-full"
      style={{ aspectRatio: reserved === null ? undefined : String(reserved) }}
    >
      <canvas
        ref={canvasRef}
        data-testid="artifact-pdf-page"
        role="img"
        aria-label={`Page ${pageNumber}`}
        className="block w-full"
      />
      <div ref={textContainerRef} className="textLayer" />
    </div>
  );
}

/**
 * Every page of a PDF, drawn with pdf.js and stacked in one scroll container. The pages are canvases
 * rather than the browser's own viewer because that viewer is a plugin Chromium will not start inside a
 * sandboxed frame — the SPA runs in the Teams tab iframe — and WebKit leaves a `blob:` PDF frame blank.
 * A pdf.js text layer over each canvas brings back selection, copy and Ctrl+F. A document that fails to
 * load falls back to the download prompt.
 */
export function PdfDocumentView({
  url,
  name,
  fill = false,
}: {
  url: string;
  name: string;
  fill?: boolean;
}): JSX.Element {
  const state = usePdfDocument(url);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerWidth = useContainerWidth(containerRef);
  const document = state.status === 'ready' ? state.document : null;
  const [fallbackAspectRatio, rememberAspectRatio] =
    useFallbackAspectRatio(document);
  if (state.status === 'error') {
    return <NoPreview name={name} contentType="application/pdf" />;
  }
  const pageNumbers =
    document === null
      ? []
      : Array.from(Array(document.numPages).keys(), (index) => index + 1);
  return (
    <div
      ref={containerRef}
      aria-label={name}
      aria-busy={state.status === 'loading'}
      className={cn(
        'w-full overflow-auto rounded-lg border border-border bg-white',
        fill ? 'h-full' : 'h-[70vh]',
      )}
    >
      {state.status === 'loading' && (
        <Skeleton className="h-full w-full rounded-lg" />
      )}
      {state.status === 'ready' &&
        pageNumbers.map((pageNumber) => (
          <PdfPage
            key={pageNumber}
            opened={state}
            pageNumber={pageNumber}
            containerWidth={containerWidth}
            fallbackAspectRatio={fallbackAspectRatio}
            onPageArrived={rememberAspectRatio}
          />
        ))}
    </div>
  );
}
